import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectScrollDownButton,
  SelectScrollUpButton,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
} from "@design-system/shadcn";
import React, { useCallback, useMemo } from "react";

import { useFieldContext } from "../core/form-contexts";

type FormFieldSelectProps = Omit<
  React.ComponentProps<typeof Select>,
  "name" | "value" | "onValueChange"
>;

function FormFieldSelect({ children, ...props }: FormFieldSelectProps) {
  const field = useFieldContext<string>();

  if (!field) {
    throw new Error("FormFieldSelect must be used within form.AppField");
  }

  const changeHandler = useCallback(
    (value: string) => field.handleChange(value),
    [field]
  );

  return (
    <Select
      name={field.name}
      value={field.state.value}
      onValueChange={changeHandler}
      {...props}
    >
      {children}
    </Select>
  );
}

FormFieldSelect.Content = SelectContent;
FormFieldSelect.Group = SelectGroup;
FormFieldSelect.Item = SelectItem;
FormFieldSelect.Label = SelectLabel;
FormFieldSelect.ScrollDownButton = SelectScrollDownButton;
FormFieldSelect.ScrollUpButton = SelectScrollUpButton;
FormFieldSelect.Separator = SelectSeparator;
FormFieldSelect.Trigger = FormFieldSelectTrigger;
FormFieldSelect.Value = SelectValue;

type FormFieldSelectTriggerProps = Omit<
  React.ComponentProps<typeof SelectTrigger>,
  "hasError"
>;

function FormFieldSelectTrigger({
  children,
  ...props
}: FormFieldSelectTriggerProps) {
  const field = useFieldContext<string>();

  if (!field) {
    throw new Error("FormFieldSelectTrigger must be used within form.AppField");
  }

  const hasError = useMemo(
    () => field.state.meta.errors.length > 0,
    [field.state.meta.errors.length]
  );

  const labelId = `${field.name}-label`;

  return (
    <SelectTrigger aria-invalid={hasError} aria-labelledby={labelId} {...props}>
      {children}
    </SelectTrigger>
  );
}

export { FormFieldSelect, FormFieldSelectTrigger };
export type { FormFieldSelectProps, FormFieldSelectTriggerProps };
